import i18next, { type i18n as I18nextInstance } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next, useTranslation } from 'react-i18next';
export { T } from './T';

/**
 * The supported languages.
 * Decision: specs/DECISION_LANGUAGE_REDUCTION_2026-04-27.md (supersedes D8).
 * Order is intentional: ka first (primary market), then en, then coverage langs.
 * Keep in sync with `LangCodeSchema` in `@sazeo/schemas`.
 */
export const SUPPORTED_LANGUAGES = ['ka', 'en', 'ru', 'az', 'tr', 'uk'] as const;

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const DEFAULT_LANGUAGE: SupportedLanguage = 'ka';

/** Languages that require right-to-left layout. (None in the active set.) */
export const RTL_LANGUAGES: readonly SupportedLanguage[] = [];

export const isRtl = (lang: string): boolean => (RTL_LANGUAGES as readonly string[]).includes(lang);

export interface InitI18nOptions {
  defaultLang?: string;
  namespaces?: string[];
  extraSupportedLangs?: readonly string[];
}

const DEFAULT_NAMESPACES = ['core.auth', 'core.settings', 'core.nav'] as const;

/**
 * Initialise the shared i18next instance. Safe to call multiple times; later
 * calls will reconfigure the already-initialised instance.
 *
 * This intentionally does NOT bundle locale JSON — namespaces are loaded on
 * demand via `loadNamespace`.
 */
export async function initI18n(opts: InitI18nOptions = {}): Promise<I18nextInstance> {
  const lng = opts.defaultLang ?? DEFAULT_LANGUAGE;
  const ns = opts.namespaces ?? [...DEFAULT_NAMESPACES];
  const supportedLngs = Array.from(
    new Set<string>([...SUPPORTED_LANGUAGES, ...(opts.extraSupportedLangs ?? [])]),
  );

  if (!i18next.isInitialized) {
    await i18next
      .use(LanguageDetector)
      .use(initReactI18next)
      .init({
        lng,
        fallbackLng: DEFAULT_LANGUAGE,
        supportedLngs,
        ns,
        defaultNS: ns[0],
        interpolation: { escapeValue: false },
        resources: {},
        returnNull: false,
      });
  } else {
    await i18next.changeLanguage(lng);
  }

  return i18next;
}

/**
 * Dynamically fetch and register a namespace for a given language.
 *
 * MVP implementation: fetch `/locales/{lang}/{ns}.json` over HTTP. The host
 * app is expected to serve the `locales/` directory statically (Vite's
 * `public/` or a CF Worker asset binding).
 *
 * TODO(phase-2): wire this to Vite's `import.meta.glob` so locales are
 *                code-split and fingerprinted alongside the app bundle.
 */
export async function loadNamespace(ns: string, lang: string): Promise<void> {
  if (i18next.hasResourceBundle(lang, ns)) {
    return;
  }

  const url = `/locales/${lang}/${ns}.json`;
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`i18n: failed to load ${url} (status ${res.status})`);
  }
  const bundle = (await res.json()) as Record<string, unknown>;
  i18next.addResourceBundle(lang, ns, bundle, true, true);
}

/** Thin wrapper around `react-i18next`'s `useTranslation` bound to a single namespace. */
export function useTranslate(ns: string) {
  return useTranslation(ns);
}

export { i18next };
